import React, {useState} from "react";
import {Button} from "primereact/button";
import {TabPanel, TabView} from "primereact/tabview";
import {Divider} from "primereact/divider";
import GeneralUtils from "../../../../utils/GeneralUtils";
import {AssetService} from "../../../../service/AssetService";
import ExternalUrlForm from "./components/ExternalUrlForm";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import Toggle from "./components/Toogle";
import {Card} from "primereact/card";
import MultiFileUploader from "../../../../components/fileUploader/MultiFileUploader";
import FileService from "../../../../service/FileService";
import {toast} from 'react-toastify';
import Loading from "../../../../components/loading/Loading";
import {LectureService} from "../../../../service/LectureService";

/**
 * SupplementaryAsset - Yardımcı kaynaklar için component
 *
 * @param {Object} props
 * @param {Object} props.lecture - Lecture objesi
 * @param {string} props.mainAssetType - Ana içerik tipi (disabled kontrolü için)
 */
const SupplementaryAsset = (props) => {
    const {lecture, mainAssetType} = props;
    const [supplementaryAssets, setSupplementaryAssets] = useState(lecture?.supplementaryAssets || []);
    const [supplementaryAssetsToDelete, setSupplementaryAssetsToDelete] = useState(null);
    const [toggleSupplementaryAssets, setToggleSupplementaryAssets] = useState(false);
    const [loading, setLoading] = useState(false);

    const service = new AssetService();
    const fileService = new FileService();
    const lectureService = new LectureService();

    const saveURL = (asset) => {
        asset.assetType = 'ExternalLink';
        asset.isExternal = true;
        asset.lecture = lecture;

        service.create(asset).then((response) => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                setToggleSupplementaryAssets(false);
                // Yeni asset'i listeye ekle
                setSupplementaryAssets(prev => [...prev, response]);
            }
        });
    };

    const deleteSupplementaryAsset = (id) => {
        service.delete(id).then((response) => {
            if (!GeneralUtils.isNullOrEmpty(response) &&
                GeneralUtils.isNullOrEmpty(response.type) &&
                GeneralUtils.isNullOrEmpty(response.error)) {
                // Asset'i listeden kaldır
                setSupplementaryAssets(prev => prev.filter(asset => asset.id !== id));
            }
        });
    };

    const onUploadFiles = (files) => {
        if (!files || files.length === 0) return;

        setLoading(true);
        const uploadPromises = files.map(file => {
            if (file.size > 1024 * 1024 * 1024) {
                toast.warn(`${file.name}: Dosya boyutu 1 GB'dan küçük olmalıdır!`);
                return Promise.resolve(null);
            }

            return fileService.upload(getFileForm(file)).then((response) => {
                if (!GeneralUtils.isNullOrEmpty(response) &&
                    (GeneralUtils.isNullOrEmpty(response.type) || response.type === "SUCCESS") &&
                    GeneralUtils.isNullOrEmpty(response.error)) {
                    return response;
                }
                return null;
            });
        });

        Promise.all(uploadPromises).then((responses) => {
            const successfulUploads = responses.filter(response =>
                !GeneralUtils.isNullOrEmpty(response)
            );

            if (successfulUploads.length > 0) {
                // Refresh lecture data to get updated supplementary assets
                setToggleSupplementaryAssets(false);
                toast.success(`${successfulUploads.length} dosya başarıyla yüklendi!`);

                lectureService.findById(lecture.id).then((response) => {
                    if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                        setSupplementaryAssets(response?.supplementaryAssets);
                    }
                });
            }
            setLoading(false);
        }).catch(error => {
            console.error("Upload error:", error);
            setLoading(false);
            toast.error("Dosya yükleme sırasında bir hata oluştu!");
        });
    };

    const getFileForm = (file) => {
        const form = new FormData();
        form.append('file', file);
        form.append('name', file.name);
        form.append('path', 'lecture-supplementary');
        form.append('id', lecture.id);
        return form;
    }

    const renderAsset = (asset) => {
        if (asset.assetType === 'ExternalLink') {
            return (
                <a href={asset.externalUrl} target="_blank" rel="noopener noreferrer"
                   className="flex flex-row gap-2 align-items-center font-normal text-sm">
                    <i className="pi pi-external-link"></i>
                    <div className="m-0 text-overflow-ellipsis overflow-hidden white-space-nowrap" title={asset.title}>
                        {asset.title}
                    </div>
                </a>
            );
        } else if (asset.assetType === 'File') {
            return (
                <a href={asset.downloadUrl} target="_blank" rel="noopener noreferrer"
                   className="flex flex-row gap-2 align-items-center font-normal text-sm">
                    <i className="pi pi-file"></i>
                    <div className="m-0 text-overflow-ellipsis overflow-hidden white-space-nowrap" title={asset.title}>
                        {asset.title}
                    </div>
                </a>
            );
        } else if (asset.assetType === 'SourceCode') {
            return (
                <a href={asset.downloadUrl} target="_blank" rel="noopener noreferrer"
                   className="flex flex-row gap-2 align-items-center font-normal text-sm">
                    <i className="pi pi-code"></i>
                    <div className="m-0 text-overflow-ellipsis overflow-hidden white-space-nowrap" title={asset.title}>
                        {asset.title}
                    </div>
                </a>
            );
        }

        return null;
    };

    const renderView = (asset, index) => (
        <div key={`asset-${index}`} className="flex flex-row gap-2 align-items-center relative">
            {renderAsset(asset)}

            <div className="absolute right-0 gap-4 align-items-center">
                <Button
                    icon="pi pi-fw pi-trash"
                    rounded
                    text
                    severity="danger"
                    onClick={() => setSupplementaryAssetsToDelete(asset)}
                />
            </div>

        </div>
    );

    return (
        <>
            {supplementaryAssets?.length > 0 && (
                <>
                    <Divider/>
                    <span style={{minWidth: "50px", fontWeight: "600"}}>Yardımcı Kaynaklar: </span>
                    {supplementaryAssets.map((asset, index) =>
                        <Card key={index} title={renderView(asset, index)} className="question-editor noshadow"/>)
                    }
                </>
            )}

            <Toggle
                label="Yardımcı Kaynak Ekle"
                visible={toggleSupplementaryAssets}
                onHide={() => setToggleSupplementaryAssets(!toggleSupplementaryAssets)}
                showCard={true}
                cardClassName="supplementary-assets-form noshadow"
                buttonClassName="w-full"
                disabled={GeneralUtils.isNullOrEmpty(mainAssetType)}
            >
                {loading && <Loading/>}
                <TabView>
                    <TabPanel header="Dış Kaynak URL">
                        <ExternalUrlForm
                            cancel={() => setToggleSupplementaryAssets(false)}
                            save={(asset) => saveURL(asset)}
                        />
                    </TabPanel>
                    <TabPanel header="İndirilebilir Dosya">
                        <MultiFileUploader
                            name="downloadable-files"
                            label="Dosyaları Yükle"
                            accept="*/*"
                            note="Not: Kaynak, derste öğrencilere yardımcı olmak için kullanılabilecek her türlü doküman olabilir. Bu dosya, bir ders ekstrası olarak görünecektir. Her şeyin okunaklı olduğundan ve dosya boyutunun en fazla 1 GiB olduğundan emin olun. Birden fazla dosya aynı anda yüklenebilir."
                            onUpload={(files) => onUploadFiles(files)}
                        />
                    </TabPanel>
                    <TabPanel header="Kaynak Kodu">
                        <MultiFileUploader
                            name="source-code-files"
                            label="Kaynak Kodlarını Yükle"
                            accept=".java,.py"
                            note="Not: Şimdilik yalnızca Java ve Python için kullanılabilir. .java, .py formatındaki dosyaları yükleyebilirsiniz. Birden fazla dosya aynı anda yüklenebilir. "
                            onUpload={(files) => onUploadFiles(files)}
                        />
                    </TabPanel>
                </TabView>
            </Toggle>

            
            <DeleteConfirmDialog
                itemToDelete={supplementaryAssetsToDelete}
                onHide={() => setSupplementaryAssetsToDelete(null)}
                onConfirm={deleteSupplementaryAsset}
            />
        </>
    );
};

export default SupplementaryAsset;
